import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { BottomTabBar, Sidebar } from '../components';
import type { TabId } from '../components/navigation/Sidebar';
import { ImpersonationBanner } from '../components/feedback/ImpersonationBanner';
import { ToastContainer } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const TAB_ROUTES: Record<TabId, string> = {
  home: '/app/home',
  invites: '/app/invites',
  guests: '/app/guests',
  'table-plan': '/app/table-plan',
  settings: '/app/settings',
};

const ROUTE_TO_TAB: Record<string, TabId> = {
  '/app/home': 'home',
  '/app/invites': 'invites',
  '/app/guests': 'guests',
  '/app/table-plan': 'table-plan',
  '/app/settings': 'settings',
};

// Context for child components to control sidebar
interface AppLayoutContextType {
  collapseSidebar: () => void;
  expandSidebar: () => void;
  preventAutoCollapse: () => void;
  allowAutoCollapse: () => void;
  setRightPanelWidth: (w: number) => void;
}

const AppLayoutContext = createContext<AppLayoutContextType | null>(null);

export function useAppLayout() {
  return useContext(AppLayoutContext);
}

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Redirect Etsy users into their pre-created invite the first time they enter the app
  useEffect(() => {
    const etsyInviteId = (user?.preferences as any)?.etsy_invite_id;
    if (!etsyInviteId) return;
    const alreadyThere = location.pathname === `/app/invites/${etsyInviteId}/edit`;
    if (alreadyThere) return;
    navigate(`/app/invites/${etsyInviteId}/edit?etsy=1`, { replace: true });
  }, [user?.preferences]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-collapse sidebar on builder/edit routes (and social card builder)
  const isBuilderRoute = (location.pathname.includes('/invites/') && location.pathname.includes('/edit'))
    || location.pathname === '/app/admin/social'
    || location.pathname.startsWith('/app/admin/social/')
    || location.pathname === '/app/admin/emails'
    || location.pathname === '/app/admin/blog'
    || location.pathname.startsWith('/app/admin/blog/')
    || location.pathname === '/app/table-plan';

  // Collapse by default on tablet (md–lg), expand on large desktop
  const isTablet = () => typeof window !== 'undefined' && window.innerWidth < 1024;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => isTablet());
  const [autoCollapseAllowed, setAutoCollapseAllowed] = useState(true);

  // Keep collapsed state in sync with viewport resizes
  const isBuilderRouteRef = useRef(isBuilderRoute);
  useEffect(() => { isBuilderRouteRef.current = isBuilderRoute; }, [isBuilderRoute]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024 && !isBuilderRouteRef.current) {
        setSidebarCollapsed(false);
      } else if (window.innerWidth >= 768 && window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Auto-collapse when entering builder (if allowed), auto-expand when leaving
  useEffect(() => {
    if (isBuilderRoute && autoCollapseAllowed) {
      setSidebarCollapsed(true);
    } else if (!isBuilderRoute) {
      setSidebarCollapsed(isTablet());
      setAutoCollapseAllowed(true); // Reset when leaving builder
    }
  }, [isBuilderRoute, autoCollapseAllowed]);

  const [rightPanelWidth, setRightPanelWidth] = useState(0);
  const collapseSidebar = useCallback(() => setSidebarCollapsed(true), []);
  const expandSidebar = useCallback(() => setSidebarCollapsed(false), []);
  const preventAutoCollapse = useCallback(() => setAutoCollapseAllowed(false), []);
  const allowAutoCollapse = useCallback(() => setAutoCollapseAllowed(true), []);

  // Determine active tab from current route
  // For nested invite routes, highlight "invites"
  const activeTab: TabId = location.pathname.startsWith('/app/invites')
    ? 'invites'
    : (ROUTE_TO_TAB[location.pathname] ?? 'home');

  // Check if we're on a nested route (hide bottom tabs on mobile)
  const isNestedRoute =
    location.pathname === '/app/admin' ||
    location.pathname === '/app/profile' ||
    location.pathname === '/app/admin/social' ||
    location.pathname.startsWith('/app/admin/social/') ||
    location.pathname === '/app/admin/outreach' ||
    location.pathname === '/app/admin/emails' ||
    location.pathname === '/app/admin/blog' ||
    location.pathname.startsWith('/app/admin/blog/') ||
    location.pathname === '/app/admin/tickets' ||
    (location.pathname.includes('/invites/') && (location.pathname.includes('/edit') || location.pathname.includes('/guests')));

  const handleTabChange = (tab: TabId) => {
    navigate(TAB_ROUTES[tab]);
  };

  const contextValue: AppLayoutContextType = {
    collapseSidebar,
    expandSidebar,
    preventAutoCollapse,
    allowAutoCollapse,
    setRightPanelWidth,
  };

  return (
    <AppLayoutContext.Provider value={contextValue}>
      <div className={`${isBuilderRoute ? 'overflow-hidden' : 'min-h-screen'} bg-background`}>
        <ImpersonationBanner />

        {/* Sidebar — hidden on tablet in builder (icon rail replaces it), always shown on desktop */}
        <div className={isBuilderRoute ? 'hidden lg:block' : 'hidden md:block'}>
          <Sidebar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            collapsed={sidebarCollapsed}
            onCollapsedChange={setSidebarCollapsed}
          />
        </div>

        {/* Main content area — no left margin on tablet in builder (sidebar hidden) */}
        <main
          className={`
            ${isNestedRoute ? '' : 'pb-20 md:pb-0'}
            ${isBuilderRoute
              ? (sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64')
              : (sidebarCollapsed ? 'md:ml-[72px]' : 'md:ml-64')
            }
            transition-[margin,padding-right] duration-300 ease-in-out
          `}
          style={rightPanelWidth > 0 ? { paddingRight: rightPanelWidth } : undefined}
        >
          <Outlet />
        </main>

        {/* Bottom tab bar for mobile only (hidden on tablet and up, and nested routes) */}
        {!isNestedRoute && (
          <div className="md:hidden">
            <BottomTabBar activeTab={activeTab} onTabChange={handleTabChange} />
          </div>
        )}

        <ToastContainer />
      </div>
    </AppLayoutContext.Provider>
  );
}
